import React, {useCallback} from 'react'
import {
  BENEFICIAL_OWNER_POLITICALLY_EXPOSED_PAN_CARD_DOCUMENT,
  BENEFICIAL_OWNER_POLITICALLY_EXPOSED_PERSON_UTILITY_BILL_DOCUMENT,
  BENEFICIAL_OWNER_COUNTRY,
  BENEFICIAL_OWNER_NATIONALITY,
  BENEFICIAL_OWNER_ADDITIONAL_NATIONALITIES,
  COMPANY_ULTIMATE_BENEFICIAL_OWNERS,
  shouldRenderUBOPanCardDocumentFilter,
  PersonDocumentUploadWithInputContainer,
  ExistingPersonInputRow,
  AddressForm,
  InputTooltipInfo,
} from '@modifi/plugin-onboarding'
import {Col, Row, H5} from '@modifi/ui'

import PersonForm from '../../../../../features/person-form'
import {FormUploadComponentCol, ExistingFilesList} from '../../../../../plugins/onboarding'
import {PoliticallyExposedInputRow, UboReasonInputRow} from '../input-rows'
import {FormRow} from '../../../../../ui/form'
import ProgressSteps from '../../../../../component/progress-steps'
import {getCompanyRepProgressStepsLabels} from '../../../utils'
import {SelectOption, TranslateFn} from '../../../../../types/common'

// @Todo move to onboarding package once its safe to merge
const BENEFICIAL_OWNER_VISA_DOCUMENT = 'ultimateBeneficialOwner.visaDocument'

const shouldRenderUBOVisaDocFilter = (data: Record<string, any>): boolean =>
  data[BENEFICIAL_OWNER_COUNTRY] &&
  data[BENEFICIAL_OWNER_NATIONALITY] &&
  data[BENEFICIAL_OWNER_COUNTRY]?.toUpperCase() === 'AE' &&
  data[BENEFICIAL_OWNER_NATIONALITY]?.toUpperCase() !== 'AE' &&
  !data[BENEFICIAL_OWNER_ADDITIONAL_NATIONALITIES]?.includes('ae')

type BeneficialOwnersFormContentProps = {
  t: TranslateFn
  isNewBeneficialOwner?: boolean
  selectPersonAsDataSource: (value: string) => void
  dataSourceOptions: SelectOption[]
  hasAnyRoleFieldItem: boolean
  currentPage: number
}

const BeneficialOwnersFormContent: React.FC<BeneficialOwnersFormContentProps> = ({
  isNewBeneficialOwner = false,
  selectPersonAsDataSource,
  t,
  dataSourceOptions,
  currentPage,
  hasAnyRoleFieldItem,
}) => {
  const showPersonForm = useCallback(() => {
    if (isNewBeneficialOwner) {
      if (
        (dataSourceOptions.length > 0 && currentPage === 2) ||
        (dataSourceOptions.length < 1 && currentPage === 1)
      ) {
        return true
      }
    } else if (currentPage === 1) {
      return true
    }
    return false
  }, [currentPage, dataSourceOptions?.length, isNewBeneficialOwner])

  const showRoleFields = useCallback(() => {
    if (
      (currentPage === 2 && dataSourceOptions.length > 1) ||
      (currentPage === 1 && dataSourceOptions.length < 1)
    ) {
      return true
    }
    return false
  }, [currentPage, dataSourceOptions?.length])

  const showResidenceFields = useCallback(() => {
    if (currentPage === 3 || (currentPage === 2 && dataSourceOptions.length < 1)) {
      return true
    }
    return false
  }, [currentPage, dataSourceOptions?.length])

  return (
    <>
      <ProgressSteps
        totalSteps={
          dataSourceOptions.length > 0 && isNewBeneficialOwner && hasAnyRoleFieldItem ? 3 : 2
        }
        currentStep={currentPage}
        labelSteps={getCompanyRepProgressStepsLabels(
          dataSourceOptions.length > 0 && !!isNewBeneficialOwner
        )}
      />
      {/* If beneficial owner exists do not show the drop down for legal rep */}
      {isNewBeneficialOwner && dataSourceOptions.length > 0 && currentPage === 1 && (
        <ExistingPersonInputRow
          t={t}
          onSelectPerson={selectPersonAsDataSource}
          options={dataSourceOptions}
          personRole={COMPANY_ULTIMATE_BENEFICIAL_OWNERS}
        />
      )}

      {showPersonForm() && <PersonForm t={t} formType={COMPANY_ULTIMATE_BENEFICIAL_OWNERS} />}

      {showRoleFields() && (
        <>
          <UboReasonInputRow t={t} />

          <PoliticallyExposedInputRow t={t} />

          <FormRow>
            <FormUploadComponentCol
              t={t}
              inputName={BENEFICIAL_OWNER_POLITICALLY_EXPOSED_PAN_CARD_DOCUMENT}
              uploadFormComponent={PersonDocumentUploadWithInputContainer}
              filterFunc={shouldRenderUBOPanCardDocumentFilter}
              colProps={{md: 12}}
              inputProps={{
                labelAddition: (
                  <InputTooltipInfo
                    inputName={BENEFICIAL_OWNER_POLITICALLY_EXPOSED_PAN_CARD_DOCUMENT}
                    t={t}
                  />
                ),
                components: {
                  existingFilesList: ExistingFilesList,
                },
              }}
            />
          </FormRow>
        </>
      )}

      {showResidenceFields() && (
        <>
          <Row mb={16} mt={8}>
            <Col>
              <H5>{t('person-form.address.headline')}</H5>
            </Col>
          </Row>
          <Row>
            <Col>
              <AddressForm
                formType={COMPANY_ULTIMATE_BENEFICIAL_OWNERS}
                components={{
                  salutation: null,
                  firstName: null,
                  middleName: null,
                  lastName: null,
                }}
              />
            </Col>
          </Row>
          <FormRow>
            <FormUploadComponentCol
              t={t}
              inputName={BENEFICIAL_OWNER_VISA_DOCUMENT}
              uploadFormComponent={PersonDocumentUploadWithInputContainer}
              filterFunc={shouldRenderUBOVisaDocFilter}
              colProps={{md: 12}}
              inputProps={{
                labelAddition: (
                  <InputTooltipInfo
                    inputName={BENEFICIAL_OWNER_POLITICALLY_EXPOSED_PERSON_UTILITY_BILL_DOCUMENT}
                    t={t}
                  />
                ),
                components: {
                  existingFilesList: ExistingFilesList,
                },
              }}
            />

            <FormUploadComponentCol
              t={t}
              inputName={BENEFICIAL_OWNER_POLITICALLY_EXPOSED_PERSON_UTILITY_BILL_DOCUMENT}
              uploadFormComponent={PersonDocumentUploadWithInputContainer}
              colProps={{md: 12}}
              inputProps={{
                labelAddition: (
                  <InputTooltipInfo
                    inputName={BENEFICIAL_OWNER_POLITICALLY_EXPOSED_PERSON_UTILITY_BILL_DOCUMENT}
                    t={t}
                  />
                ),
                components: {
                  existingFilesList: ExistingFilesList,
                },
              }}
            />
          </FormRow>
        </>
      )}
    </>
  )
}

export default BeneficialOwnersFormContent
